import { useCallback, useEffect, useMemo, useState } from "react";
import "./ProjectsPage.css";
import { AppShell } from "../app/AppShell";
import { useMe } from "../app/AuthGate";
import { Button } from "../components/Button";
import { Alert, EmptyState, Loading } from "../components/Card";
import { PageHead } from "../components/Page";
import { Chip } from "../components/Status";
import { Input } from "../components/Field";
import { ProjectCards, ProjectForm, ProjectIssueStats, ProjectRepoBind, ProjectsEmpty } from "../components/Projects";
import type { RepoRef } from "../components/Projects";
import { authErrText } from "../lib/auth";
import { projectApi } from "../lib/project";
import type { Project } from "../lib/project";
import { tasksApi } from "../lib/tasks";
import type { Issue } from "../lib/tasks";
import { useRouteParam } from "../lib/route";
import { tx } from "../i18n";

/* ══ 일감 › 프로젝트(t-in-port-work-projects) ══
   목록(카드)과 상세(`#/projects?p=<id>`) 두 상태다. 예전 git 저장소 화면 아래 표를 걷고 여기로 옮겼다 —
   프로젝트는 일감의 축이고, 리포 묶기는 상세에서 한다. 쓰기(만들기 · 이름 · 묶기 · 지우기)는 `/admin/` 문이라
   관리자에게만 단추를 그린다. 읽기는 로그인한 누구나다. */

/** 빠른 만들기가 쓰는 이름 · id — 옛 설치를 옮긴 0024 와 같은 값이라, 어느 길로 섰든 같은 프로젝트로 보인다. */
const QUICK_ID = "default";

export function ProjectsPage() {
  const auth = useMe();
  const canEdit = !auth || auth.me.role === "admin";
  const pid = useRouteParam("p");
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [repos, setRepos] = useState<RepoRef[]>([]);
  const [issues, setIssues] = useState<Issue[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [armed, setArmed] = useState(false);

  const load = useCallback(() => {
    projectApi.list().then(
      (ps) => {
        setProjects(ps);
        setLoadErr(null);
      },
      (e) => setLoadErr(tx("project.loadFailed", { err: authErrText(e) })),
    );
    // 리포 이름 · 이슈 현황은 곁가지다 — 못 받아도 카드는 그린다(리포는 id 로, 현황은 숨긴다).
    projectApi.repos().then((rs) => setRepos(rs.map((r) => ({ id: r.id, label: `${r.owner}/${r.name}` }))), () => undefined);
    tasksApi.list().then(setIssues, () => setIssues(null));
  }, []);
  useEffect(load, [load]);
  // 상세를 오가면 펼친 편집을 접는다 — 다른 프로젝트에 남은 「지우기」가 눌리면 안 된다.
  useEffect(() => {
    setRenaming(null);
    setArmed(false);
    setErr(null);
  }, [pid]);

  const repoLabel = useCallback((rid: string) => repos.find((r) => r.id === rid)?.label ?? rid, [repos]);
  const list = projects ?? [];
  const cur = pid ? list.find((p) => p.id === pid) ?? null : null;
  const statuses = useMemo(() => (issues ?? []).filter((t) => cur && t.projectId === cur.id && t.childCount === 0).map((t) => t.status), [issues, cur]);

  /* 이슈 현황은 **잎 이슈**만 센다 — 대시보드 진행률과 같은 셈(부모 상태는 하위에서 파생된다). */
  /** 서버가 준 답을 그린다(보낸 값을 그리지 않는다). */
  function put(p: Project) {
    setProjects((xs) => {
      const l = xs ?? [];
      return l.some((x) => x.id === p.id) ? l.map((x) => (x.id === p.id ? p : x)) : [...l, p];
    });
  }
  async function run(key: string, f: () => Promise<void>) {
    setBusy(key);
    setErr(null);
    try {
      await f();
    } catch (e) {
      setErr(authErrText(e));
    } finally {
      setBusy(null);
    }
  }

  const create = (name: string, id: string) =>
    run("create", async () => {
      const p = await projectApi.create({ name, id: id || undefined });
      put(p);
      setAdding(false);
      location.hash = `#/projects?p=${encodeURIComponent(p.id)}`;
    });
  const quick = () =>
    run("quick", async () => {
      put(await projectApi.create({ id: QUICK_ID, name: tx("project.quickName"), repositoryIds: repos.map((r) => r.id) }));
    });

  if (pid) {
    return (
      <AppShell route="/projects">
        <p className="pback">
          <a href="#/projects">← {tx("projects.back")}</a>
        </p>
        {loadErr ? <Alert tone="error">{loadErr}</Alert> : null}
        {!projects && !loadErr ? <Loading text={tx("common.state.loading")} /> : null}
        {projects && !cur ? <EmptyState title={tx("projects.missing", { id: pid })} desc={tx("projects.missingDesc")} /> : null}
        {cur ? (
          <>
            <PageHead
              title={
                renaming !== null ? (
                  <span className="prename">
                    <Input
                      size="md"
                      aria-label={tx("project.name")}
                      value={renaming}
                      autoFocus
                      onChange={(e) => setRenaming(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") setRenaming(null);
                        if (e.key === "Enter" && renaming.trim())
                          void run("rename", async () => {
                            put(await projectApi.rename(cur.id, renaming.trim()));
                            setRenaming(null);
                          });
                      }}
                    />
                  </span>
                ) : (
                  cur.name
                )
              }
              meta={<Chip tone="muted">{cur.id}</Chip>}
              actions={
                canEdit ? (
                  renaming !== null ? (
                    <>
                      <Button size="md" variant="ghost" onClick={() => setRenaming(null)}>
                        {tx("common.action.cancel")}
                      </Button>
                      <Button
                        size="md"
                        variant="primary"
                        disabled={!renaming.trim()}
                        loading={busy === "rename"}
                        onClick={() =>
                          run("rename", async () => {
                            put(await projectApi.rename(cur.id, renaming.trim()));
                            setRenaming(null);
                          })
                        }
                      >
                        {tx("common.action.save")}
                      </Button>
                    </>
                  ) : armed ? (
                    <>
                      <Button size="md" variant="ghost" onClick={() => setArmed(false)}>
                        {tx("common.action.cancel")}
                      </Button>
                      <Button
                        size="md"
                        variant="danger"
                        loading={busy === "remove"}
                        onClick={() =>
                          run("remove", async () => {
                            await projectApi.remove(cur.id);
                            setProjects((xs) => (xs ?? []).filter((x) => x.id !== cur.id));
                            location.hash = "#/projects";
                          })
                        }
                      >
                        {tx("project.removeArmed")}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button size="md" variant="ghost" onClick={() => setRenaming(cur.name)}>
                        {tx("project.rename")}
                      </Button>
                      <Button size="md" variant="ghost" onClick={() => setArmed(true)}>
                        {tx("project.remove")}
                      </Button>
                    </>
                  )
                ) : null
              }
            />
            {err ? <Alert tone="error">{err}</Alert> : null}
            <div className="pdetail">
              {issues ? <ProjectIssueStats statuses={statuses} boardHref="#/board" /> : null}
              <ProjectRepoBind
                p={cur}
                repos={repos}
                canEdit={canEdit}
                busy={busy === "bind"}
                onToggle={(rid) =>
                  run("bind", async () => {
                    const next = cur.repositoryIds.includes(rid) ? cur.repositoryIds.filter((x) => x !== rid) : [...cur.repositoryIds, rid];
                    put(await projectApi.setRepos(cur.id, next));
                  })
                }
              />
            </div>
            <p className="pfoot">
              {tx("projects.msLine", { n: cur.milestoneCount })} <a href="#/milestones">{tx("projects.toMilestones")} →</a>
              {canEdit ? <> · {tx("projects.removeNote")}</> : null}
            </p>
          </>
        ) : null}
      </AppShell>
    );
  }

  return (
    <AppShell route="/projects">
      <PageHead
        title={tx("project.title")}
        lead={tx("projects.lead")}
        meta={<Chip tone="muted">{projects ? tx("project.count", { n: list.length }) : "…"}</Chip>}
        actions={
          canEdit && !adding ? (
            <Button size="md" variant="primary" onClick={() => setAdding(true)}>
              ＋ {tx("projects.new")}
            </Button>
          ) : null
        }
      />
      {loadErr ? <Alert tone="error">{loadErr}</Alert> : null}
      {err ? <Alert tone="error">{err}</Alert> : null}
      {adding ? <ProjectForm busy={busy === "create"} onCreate={create} onCancel={() => setAdding(false)} /> : null}
      {!projects && !loadErr ? <Loading text={tx("common.state.loading")} /> : null}
      {projects && !list.length ? <ProjectsEmpty repos={repos.length} canEdit={canEdit} busy={busy === "quick"} onQuick={quick} /> : null}
      {list.length ? <ProjectCards projects={list} repoLabel={repoLabel} /> : null}
    </AppShell>
  );
}
